import React from 'react';
import {
   Bell,
   BookOpen,
   Briefcase as BriefCaseIcon,
   Cloud,
   DollarSign as DollarSignIcon,
   List,
   Monitor,
   Rss,
   UserPlus as UserPlusIcon,
   Users as UsersIcon,
} from 'react-feather';
import lodash from 'lodash';
import getAccessByURLPath from 'constants/pageAccess';
import { role, access } from '../../constants';
import getIsPageReadyWithAppEnvByURLPath from '../../constants/pageEnvironmentAccess';
import { partnerRole } from '../../constants/roles';


const fontAwesomeFontSize = '1.25em';

const withFontAwesomeIcon = (iconClassName = '') => (prop) => (
   <span style={{ fontSize: fontAwesomeFontSize }}>
      <i className={`${ iconClassName } ${ prop.className ? prop.className : '' }`}/>
   </span>
);

export const sideNavLinkType = {
   employer: 'EMPLOYER',
   partner: 'PARTNER',
   admin: 'ADMIN',
};

/**
   All nodes must have access property defined
   in order to make the links visible.
 * */
const navItemAccess = {
   partner: {
      jobFeed: {
         type: access.type.role,
         value: {
            partner: [
               role.partner.admin,
               role.partner.employerCentralExternal,
               role.partner.employerCentralInternal,
            ],
         },
      },
      synJobFeed: {
         type: access.type.role,
         value: {
            partner: [
               role.partner.admin,
               role.partner.jobFeedReportExternal,
               role.partner.jobFeedReportInternal,
            ],
         },
      },
      synSites: {
         type: access.type.role,
         value: {
            partner: [
               role.partner.admin,
               role.partner.syndicatedSitesPartnerExternal,
               role.partner.syndicatedSitesPartnerInternal,
            ],
         },
      },
   },
   admin: {
      tools: {
         rules: {
            type: access.type.role,
            value: {
               partner: [
                  role.partner.admin,
                  role.partner.employerCentralInternal,
               ],
            },
         },
         searchEmulator: {
            type: access.type.role,
            value: {
               partner: [
                  role.partner.admin,
                  role.partner.jobFeedReportInternal,
               ],
            },
         },
         jobManager: {
            type: access.type.role,
            value: {
               employer: [
                  role.employer.admin,
               ],
               partner: [
                  role.partner.admin,
                  role.partner.employerCentralInternal,
               ],
            },
         },
         logos: {
            type: access.type.role,
            value: {
               partner: [
                  role.partner.admin,
                  role.partner.employerCentralInternal,
                  partnerRole.advertiserCentralInternal,
               ],
            },
         },
      },
      reports: {
         changeLog: {
            type: access.type.role,
            value: {
               employer: [
                  role.employer.admin,
               ],
               partner: [
                  role.partner.admin,
               ],
            },
         },
         syndicatedJobs: {
            type: access.type.role,
            value: {
               partner: [
                  role.partner.admin,
                  role.partner.jobFeedReportInternal,
               ],
            },
         },
      },
   },
};



const sideNavItemList = [
   // =========================================
   // ============ DIRECT EMPLOYER ============
   // =========================================
   {
      type: sideNavLinkType.employer,
      name: 'Direct Employer',
      path: null,
      deprecating: true,
      access: access.byRole.employer.all,
      subItems: [
         {
            name: 'Jobs',
            path: null,
            icon: BriefCaseIcon,
            access: access.byRole.employer.all,
            subItems: [
               {
                  path: '/employer/job',
                  name: 'Jobs',
                  deprecating: true,
                  access: access.byRole.employer.all,
               },
               {
                  path: '/employer/jobgroup',
                  name: 'Job Groups',
                  deprecating: true,
                  access: access.byRole.employer.all,
               },
               {
                  path: '/employer/company',
                  name: 'Hiring Companies',
                  deprecating: true,
                  access: access.byRole.employer.all,
               },
               {
                  path: '/employer/accountStatus',
                  name: 'Manage Jobs',
                  deprecating: true,
                  access: {
                     type: access.type.privilege,
                     value: '*:contextual_action:manage_jobs',
                  },
               },
               {
                  path: '/employer/jobreport',
                  name: 'Reporting',
                  deprecating: true,
                  access: access.byRole.employer.all,
               },
            ],
         },
         {
            name: 'Candidates',
            path: null,
            icon: UsersIcon,
            access: access.byRole.employer.all,
            subItems: [
               {
                  path: '/employer/candidate',
                  name: 'Candidates',
                  deprecating: true,
                  access: access.byRole.employer.all,
               },
               {
                  path: '/employer/messages',
                  name: 'Messages',
                  deprecating: true,
                  access: access.byRole.employer.onlyAdmin,
               },
            ],
         },
         {
            name: 'Billing',
            path: null,
            icon: DollarSignIcon,
            access: access.byRole.employer.all,
            subItems: [
               {
                  path: '/employer/billing',
                  name: 'Billing Information',
                  deprecating: true,
                  access: {
                     type: access.type.privilege,
                     value: '*:main_menu:billing',
                  },
               },
               {
                  path: '/employer/billingReport',
                  name: 'Billing Report',
                  deprecating: true,
                  access: access.byRole.employer.onlyAdmin,
               },
               {
                  path: '/employer/transactions/',
                  name: 'Transactions',
                  deprecating: true,
                  access: {
                     type: access.type.privilege,
                     value: '*:main_menu:billing',
                  },
               },
            ],
         },
         {
            name: 'Accounts',
            path: null,
            icon: UserPlusIcon,
            access: access.byRole.employer.all,
            subItems: [
               {
                  path: '/employer/account',
                  name: 'Account Details',
                  deprecating: true,
                  access: access.byRole.employer.all,
               },
               {
                  path: '/employer/myAccounts',
                  name: 'Employer Accounts',
                  deprecating: true,
                  access: {
                     type: access.type.privilege,
                     value: '*:main_menu:myAccounts',
                  },
               },
            ],
         },
      ],
   },
   // =================================
   // ============ PARTNER ============
   // =================================
   {
      type: sideNavLinkType.partner,
      name: 'Partner',
      path: '/flash',
      access: access.byRole.partner.all,
      subItems: [
         {
            name: 'Job Feed',
            path: null,
            access: navItemAccess.partner.jobFeed,
            icon: List,
            subItems: [
               {
                  name: 'Job Reporting',
                  path: '/job-feed/job-reporting',
                  access: getAccessByURLPath('/job-feed/job-reporting'),
               },
               {
                  name: 'Manage Jobs',
                  path: '/job-feed/account-status',
                  access: getAccessByURLPath('/job-feed/account-status'),
               },
               {
                  name: 'Billing',
                  access: navItemAccess.partner.jobFeed,
                  subItems: [
                     {
                        name: 'Billing Summary',
                        path: '/job-feed/billing-summary/search',
                        access: access.byRole.partner.onlyAdmin,
                     },
                     {
                        name: 'Billing Details',
                        path: '/job-feed/billing/details',
                        access: getAccessByURLPath('/job-feed/billing/details'),
                     },
                  ],
               },
               {
                  name: 'Settings',
                  path: null,
                  access: access.byRole.partner.onlyAdmin,
                  isComingSoon: true,
                  subItems: [
                     {
                        name: 'Target CPA',
                        path: '/job-feed/settings/target-cpa',
                        access: access.byRole.partner.onlyAdmin,
                     },
                     {
                        name: 'Organization Settings',
                        path: '/job-feed/settings/organization',
                        access: access.getAdminAccessByApp.jobFeed,
                     },
                     {
                        name: 'Hiring Company Settings',
                        path: '/job-feed/settings/hiring-company',
                        access: getAccessByURLPath('/job-feed/settings/hiring-company'),
                     },
                     {
                        name: 'Job Group Settings',
                        path: '/job-feed/settings/job-group',
                        access: getAccessByURLPath('/job-feed/settings/job-group'),
                     },
                  ],
               },
            ],
         },
         {
            name: 'Sponsored Ads',
            path: null,
            access: getAccessByURLPath('/sponsored-ads'),
            icon: Monitor,
            subItems: [
               {
                  name: 'Reporting',
                  path: '/sponsored-ads/reporting',
                  access: getAccessByURLPath('/sponsored-ads/reporting'),
               },
               {
                  name: 'Campaign Manager',
                  path: '/sponsored-ads/campaign-manager',
                  access: getAccessByURLPath('/sponsored-ads/campaign-manager'),
               },
               {
                  name: 'Edit Advertiser',
                  path: '/sponsored-ads/edit-advertiser',
                  access: getAccessByURLPath('/sponsored-ads/edit-advertiser'),
               },
               {
                  name: 'Edit Campaign',
                  path: '/sponsored-ads/edit-campaign',
                  access: access.getAdminAccessByApp.sponsoredAds,
               },
               {
                  name: 'Keyword Search Tool',
                  path: '/sponsored-ads/keyword-search-tool',
                  access: access.getAdminAccessByApp.sponsoredAds,
               },
               {
                  name: 'Publisher Report',
                  path: '/sponsored-ads/publisher-report',
                  access: access.getAdminAccessByApp.sponsoredAds,
               },
               {
                  name: 'Traffic Quality Report',
                  path: '/sponsored-ads/traffic-quality-report',
                  access: access.getAdminAccessByApp.sponsoredAds,
               },
               {
                  name: 'Traffic Rules',
                  access: access.getAdminAccessByApp.sponsoredAds,
                  subItems: [
                     {
                        name: 'Advertiser Rules',
                        path: '/sponsored-ads/traffic-rules/advertiser-rules',
                        access: access.getAdminAccessByApp.sponsoredAds,
                     },
                     {
                        name: 'Publisher Rules',
                        path: '/sponsored-ads/traffic-rules/publisher-rules',
                        access: access.getAdminAccessByApp.sponsoredAds,
                     },
                  ],
               },
               {
                  name: 'Billing',
                  path: null,
                  access: access.generate.roleAccess(
                     {
                        partner: [
                           role.partner.admin,
                           role.partner.advertiserCentralExternal,
                           role.partner.advertiserCentralInternal,
                        ],
                     },
                  ),
                  subItems: [
                     {
                        name: 'Billing Summary',
                        path: '/sponsored-ads/billing/summary',
                        access: access.getAdminAccessByApp.sponsoredAds,
                     },
                     {
                        name: 'Billing Details',
                        path: '/sponsored-ads/billing/details',
                        access: access.generate.roleAccess(
                           {
                              partner: [
                                 role.partner.admin,
                                 role.partner.advertiserCentralExternal,
                                 role.partner.advertiserCentralInternal,
                              ],
                           },
                        ),
                     },
                  ],
               },
            ],
         },
         {
            name: 'Syndicated Jobs',
            path: null,
            access: navItemAccess.partner.synJobFeed,
            icon: Rss,
            subItems: [
               {
                  name: 'Reporting',
                  path: '/syn-job-feed/report',
                  access: navItemAccess.partner.synJobFeed,
               },
               {
                  name: 'Conversion Reporting',
                  path: '/syn-job-feed/conversion-reporting',
                  access: getAccessByURLPath('/syn-job-feed/conversion-reporting'),
               },
               {
                  /*
                   * The full integration-doc set lives under the top-level Docs section;
                   * this entry stays as a convenience link so syndicated partners still
                   * find their docs where they always were.
                   */
                  name: 'Integration Docs',
                  path: '/syn-job-feed/integration-docs/api-docs',
                  access: navItemAccess.partner.synJobFeed,
               },
               {
                  name: 'Settings',
                  path: '/syn-job-feed/settings?active=Yes',
                  access: access.getAdminAccessByApp.synJobFeed,
               },
               {
                  name: 'XML Feed Setup',
                  path: '/syn-job-feed/xml-feed-setup',
                  deprecating: true,
                  deprecatingText: 'DEPRECATING WITH DE',
                  access: access.getAdminAccessByApp.synJobFeed,
               },
               {
                  /*
                   * ported: marks the AOE-free twins built to replace DEPRECATING (DE)
                   * pages. Renders a green NEW badge (the "(KEEP)" suffix confused
                   * people) and keeps "keep" working in nav search.
                   */
                  name: 'XML Feed Setup',
                  path: '/syn-job-feed/xml-feed-setup-keep',
                  access: access.getAdminAccessByApp.synJobFeed,
               },
               {
                  name: 'Assign Sources',
                  path: '/syn-job-feed/assign-sources',
                  access: access.getAdminAccessByApp.synJobFeed,
               },
            ],
         },
         {
            name: 'Syndicated Sponsored Ads',
            path: null,
            access: getAccessByURLPath('/syndicated-sponsored-ads'),
            icon: Cloud,
            subItems: [
               {
                  name: 'Ad Manager',
                  path: '/syndicated-sponsored-ads/ad-manager',
                  access: getAccessByURLPath('/syndicated-sponsored-ads/ad-manager'),
               },
               {
                  name: 'Edit Sponsored Partner',
                  path: '/syndicated-sponsored-ads/edit-sponsored-partner',
                  access: access.byRole.partner.onlyAdmin,
               },
               {
                  name: 'Reporting',
                  path: '/syndicated-sponsored-ads/reporting',
                  access: getAccessByURLPath('/syndicated-sponsored-ads/reporting'),
               },
            ],
         },
         {
            name: 'Docs',
            path: null,
            access: access.byRole.partner.all,
            icon: BookOpen,
            subItems: [
               {
                  name: 'Report APIs: Start Here',
                  path: '/docs/report-api-authentication',
                  access: access.byRole.partner.all,
               },
               {
                  name: 'Job Feed Report API',
                  path: '/docs/report-api',
                  access: access.byRole.partner.all,
               },
               {
                  name: 'Sponsored Ads Report API',
                  path: '/docs/sponsored-ads-report-api',
                  access: access.byRole.partner.all,
               },
               {
                  /*
                   * The syndicated integration docs stay together as one group. Routes
                   * (and their synJobFeed access) are unchanged — Syndicated Jobs keeps
                   * a convenience link to the first page.
                   */
                  name: 'Syndicated Jobs',
                  path: null,
                  access: navItemAccess.partner.synJobFeed,
                  subItems: [
                     {
                        name: 'Web API Integration',
                        path: '/syn-job-feed/integration-docs/api-docs',
                        access: navItemAccess.partner.synJobFeed,
                     },
                     {
                        name: 'Email 2-Click Integration',
                        path: '/syn-job-feed/integration-docs/email-docs',
                        access: navItemAccess.partner.synJobFeed,
                     },
                     {
                        name: 'Email Direct Integration',
                        path: '/syn-job-feed/integration-docs/direct-docs',
                        access: navItemAccess.partner.synJobFeed,
                     },
                     {
                        name: 'XML Integration',
                        path: '/syn-job-feed/integration-docs/xml-docs',
                        access: navItemAccess.partner.synJobFeed,
                     },
                  ],
               },
            ],
         },
         // LUT-508: the 'Syndicated Sites' nav bucket (Reporting /syn-sites/report, Settings
         // /syn-sites/settings) is retired from the UI. Routes are unregistered in
         // partnerRouteList.js and bookmarks redirect in PageRouteList/index.js. The
         // app/syndicatedSites module is intact — restore by reverting the LUT-508 commit.
      ],
   },
   // ===============================
   // ============ ADMIN ============
   // ===============================
   {
      name: 'Admin',
      path: null,
      type: sideNavLinkType.admin,
      access: {
         type: access.type.role,
         value: {
            employer: [
               role.employer.admin,
            ],
            partner: [
               role.partner.admin,
               role.partner.employerCentralInternal,
               role.partner.jobFeedReportInternal,
            ],
         },
      },
      subItems: [
         {
            name: 'Tools',
            path: null,
            access: {
               type: access.type.role,
               value: {
                  employer: [
                     role.employer.admin,
                  ],
                  partner: [
                     role.partner.admin,
                     role.partner.employerCentralInternal,
                  ],
               },
            },
            icon: withFontAwesomeIcon('fal fa-tools'),
            subItems: [
               {
                  name: 'Audience',
                  path: null,
                  access: access.byRole.employer.onlyAdmin,
                  subItems: [
                     {
                        path: '/employer/audience/reporting',
                        name: 'Audience Builder',
                        deprecating: true,
                        access: access.byRole.employer.onlyAdmin,
                     },
                     {
                        path: '/employer/smsLoadBalancer',
                        name: 'SMS Load Balancer',
                        deprecating: true,
                        access: access.byRole.employer.onlyAdmin,
                     },
                     {
                        path: '/employer/smsTesting',
                        name: 'SMS Testing',
                        deprecating: true,
                        access: access.byRole.employer.onlyAdmin,
                     },
                  ],
               },
               {
                  name: 'Accounts',
                  path: null,
                  access: access.byRole.employer.onlyAdmin,
                  subItems: [
                     {
                        path: '/employer/organizationCentral/',
                        name: 'DE Accounts',
                        deprecating: true,
                        access: access.byRole.employer.onlyAdmin,
                     },
                  ],
               },
               {
                  name: 'Job Manager',
                  path: null,
                  access: navItemAccess.admin.tools.jobManager,
                  subItems: [
                     {
                        path: '/employer/feedManagement',
                        name: 'Feed Management',
                        deprecating: true,
                        access: access.byRole.employer.onlyAdmin,
                     },
                     {
                        path: '/employer/feedManagement-keep',
                        name: 'Feed Management',
                        access: access.getAdminAccessByApp.jobFeed,
                     },
                     {
                        path: '/admin/job-manager/feed-management',
                        name: 'Feed Management NEW',
                        deprecating: true,
                        access: access.getAdminAccessByApp.jobFeed,
                     },
                     {
                        path: '/employer/jobContent',
                        name: 'Feed Processing Report',
                        deprecating: true,
                        access: access.byRole.employer.onlyAdmin,
                     },
                     {
                        path: '/employer/jobContent-keep',
                        name: 'Feed Processing Report',
                        access: access.getAdminAccessByApp.jobFeed,
                     },
                     {
                        path: '/employer/browser/jobTitle',
                        name: 'Job Categorization',
                        deprecating: true,
                        access: access.byRole.employer.onlyAdmin,
                     },
                     {
                        path: '/admin/job-manager/job-expire-override',
                        name: 'Feed Processor Settings',
                        access: access.getAdminAccessByApp.jobFeed,
                     },
                  ],
               },
               {
                  name: 'Logos',
                  path: null,
                  access: navItemAccess.admin.tools.logos,
                  subItems: [
                     {
                        name: 'Company Logos',
                        path: '/admin/tool/company-logos',
                        access: access.getAdminAccessByApp.jobFeed,
                     },
                     {
                        name: 'Sponsored Ad Logos',
                        path: '/admin/tool/advertiser-logos',
                        access: access.getAdminAccessByApp.sponsoredAds,
                     },
                  ],
               },
               {
                  name: 'Rules',
                  path: null,
                  access: navItemAccess.admin.tools.rules,
                  subItems: [
                     {
                        path: '/employer/advertiserRules',
                        name: 'Advertiser Rules',
                        deprecating: true,
                        deprecatingText: 'DEPRECATING WITH DE',
                        access: navItemAccess.admin.tools.rules,
                     },
                     {
                        path: '/employer/advertiserRules-keep',
                        name: 'Advertiser Rules',
                        access: navItemAccess.admin.tools.rules,
                     },
                     {
                        path: '/employer/publisherRules',
                        name: 'Publisher Rules',
                        deprecating: true,
                        deprecatingText: 'DEPRECATING WITH DE',
                        access: navItemAccess.admin.tools.rules,
                     },
                     {
                        path: '/employer/publisherRules-keep',
                        name: 'Publisher Rules',
                        access: navItemAccess.admin.tools.rules,
                     },
                     {
                        path: '/employer/layout-rule',
                        name: 'Layout Rules',
                        deprecating: true,
                        deprecatingText: 'DEPRECATING WITH DE',
                        access: navItemAccess.admin.tools.rules,
                     },
                     {
                        path: '/employer/layout-rule-keep',
                        name: 'Layout Rules',
                        access: navItemAccess.admin.tools.rules,
                     },
                     {
                        path: '/employer/content-rule-list',
                        name: 'Content Rules',
                        access: navItemAccess.admin.tools.rules,
                     },
                     {
                        path: '/employer/jobCategorizationRules',
                        name: 'Job Categorization Rules',
                        deprecating: true,
                        access: access.byRole.employer.onlyAdmin,
                     },
                     {
                        path: '/employer/jobCategorizationRules-keep',
                        name: 'Job Categorization Rules',
                        access: access.getAdminAccessByApp.jobFeed,
                     },
                     {
                        path: '/employer/feedRules',
                        name: 'Feed Rules',
                        access: navItemAccess.admin.tools.rules,
                     },
                  ],
               },
               {
                  name: 'Search Emulator',
                  path: null,
                  access: navItemAccess.admin.tools.searchEmulator,
                  subItems: [
                     {
                        name: 'Job Search Emulator',
                        path: '/admin/tool/search-emulator',
                        deprecating: true,
                        deprecatingText: 'DEPRECATING WITH DE',
                        access: navItemAccess.admin.tools.searchEmulator,
                     },
                     {
                        name: 'Job Search Emulator',
                        path: '/admin/tool/search-emulator-keep',
                        access: navItemAccess.admin.tools.searchEmulator,
                     },
                  ],
               },
            ],
         },
         {
            name: 'Reports',
            path: null,
            access: {
               type: access.type.role,
               value: {
                  employer: [
                     role.employer.admin,
                  ],
                  partner: [
                     role.partner.admin,
                     role.partner.jobFeedReportInternal,
                     role.partner.employerCentralInternal,
                     role.partner.mmrInternal,
                  ],
               },
            },
            icon: withFontAwesomeIcon('fal fa-analytics'),
            subItems: [
               {
                  name: 'Change Log',
                  path: null,
                  access: navItemAccess.admin.reports.changeLog,
                  subItems: [
                     {
                        path: '/employer/changelog/',
                        name: 'Direct Employer Legacy',
                        deprecating: true,
                        access: access.byRole.employer.onlyAdmin,
                     },
                     {
                        path: '/employer/de-changelog',
                        name: 'Direct Employer',
                        deprecating: true,
                        access: access.byRole.employer.onlyAdmin,
                     },
                     {
                        path: '/partner/partner-changelog',
                        name: 'Partner',
                        access: access.getAdminAccessByApp.jobFeed,
                     },
                     {
                        path: '/partner/partner-changelog-grouped',
                        name: 'Partner — By Record',
                        access: access.getAdminAccessByApp.jobFeed,
                     },
                  ],
               },
               {
                  path: '/admin/report/email',
                  name: 'Email Report',
                  deprecating: true,
                  access: access.byRole.employer.onlyAdmin,
               },
               // emailTriggerReportRoute(),
               // {
               //    path: '/admin/report/email',
               //    name: 'Email Report',
               //    access: access.byRole.employer.onlyAdmin,
               // },
               {
                  path: '/employer/emailReport',
                  name: 'Email Trigger Report',
                  deprecating: true,
                  access: access.byRole.employer.onlyAdmin,
               },
               {
                  path: '/employer/keyword/report',
                  name: 'Job Category Report',
                  deprecating: true,
                  deprecatingText: 'DEPRECATING WITH DE',
                  access: access.getAdminAccessByApp.manageAlert,
               },
               {
                  path: '/employer/keyword/report-keep',
                  name: 'Job Category Report',
                  access: access.getAdminAccessByApp.manageAlert,
               },
               {
                  name: 'Syndicated Jobs',
                  path: null,
                  access: navItemAccess.admin.reports.syndicatedJobs,
                  subItems: [
                     {
                        name: 'PIT Reporting',
                        path: '/admin/report/syndicated-jobs/pit',
                        access: navItemAccess.admin.reports.syndicatedJobs,
                     },
                     // {
                     //    name: 'PIT Raw Data',
                     //    path: '/admin/report/syndicated-jobs/pit-raw-data',
                     //    access: navItemAccess.admin.reports.syndicatedJobs,
                     // },
                  ],
               },
               // {
               //    name: 'Syndicated Ads',
               //    path: null,
               //    access: {
               //       type: access.type.role,
               //       value: {
               //          partner: [
               //             role.partner.admin,
               //             role.partner.jobFeedReportInternal,
               //          ],
               //       },
               //    },
               //    subItems: [
               //       {
               //          name: 'PIT Raw Data',
               //          path: '/syndicated-sponsored-ads/pit-raw-data',
               //          access: getAccessByURLPath('/syndicated-sponsored-ads'),
               //       },
               //    ],
               // },
               // {
               //    path: '/employer/traffic-quality-index',
               //    name: 'Traffic Quality Index',
               //    isComingSoon: true,
               //    access: access.byRole.employer.onlyAdmin,
               // },
               {
                  path: '/employer/report/alertltv',
                  name: 'User LTV Report',
                  deprecating: true,
                  access: access.byRole.employer.onlyAdmin,
               },
            ],
         },
      ],
   },

   // =================================
   // ============   TEMP  ============
   // =================================
   {
      type: sideNavLinkType.partner,
      name: 'TEMP',
      path: '/temp',
      access: access.byRole.partner.all,
      subItems: [
         {
            name: 'Table',
            path: null,
            access: navItemAccess.partner.jobFeed,
            icon: List,
            subItems: [
               {
                  path: '/temp/base-table',
                  name: 'Base Table',
                  access: navItemAccess.partner.jobFeed,
               },
            ],
         },
      ],
   },
];

const generateQueueItem = (node, parentNode, nodeIndexAsChild) => {
   return ({
      node,
      parentNode,
      nodeIndexAsChild, // node index in parentNode.subItems
   });
};

// Modifies tree in place
const pruneTree = (tree) => {
   const queue = [];

   try {
      const indexToPruneList = [];
      for (let i = tree.length - 1; i >= 0; --i) {
         const node = tree[i];
         const isPageReady = node.path
            ? getIsPageReadyWithAppEnvByURLPath(node.path)
            : true;

         if (isPageReady) {
            queue.push(generateQueueItem(node, null, i));
         } else {
            indexToPruneList.push(i);
         }
      }

      for (let i = 0; i < indexToPruneList.length; ++i) {
         tree.splice(indexToPruneList[i], 1);
      }
   } catch (e) {
      throw new Error('Error removing base nodes for sideNavConfig.');
   }

   while (queue.length > 0) {
      const { node, parentNode, nodeIndexAsChild } = queue.shift();
      try {
         const isPageReady = node.path
            ? getIsPageReadyWithAppEnvByURLPath(node.path)
            : true;

         if (isPageReady) {
            if (node?.subItems && lodash.isArray(node?.subItems)) {
               if (node?.subItems.length > 0) {
                  for (let i = node?.subItems.length - 1; i >= 0; --i) {
                     queue.push(generateQueueItem(node.subItems[i], node, i));
                  }
               }
            }
         } else {
            parentNode.subItems.splice(nodeIndexAsChild, 1);
         }
      } catch (e) {
         throw new Error(`Error pruning nodes from sideNavConfig. With node.name=${ node.name } node.path=${ node.path }`);
      }
   }
};

pruneTree(sideNavItemList);

export default sideNavItemList;
